"use client";

/* eslint-disable react-hooks/set-state-in-effect -- localStorage restores the last route after hydration */

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { CalendarDays, FolderOpen, House, MessageSquare, UserRound } from "lucide-react";

import clsx from "clsx";
import { useI18n } from "@/components/useI18n";
const items = [
  { href: "/app/all/chat", label: "チャット", Icon: MessageSquare },
  { href: "/app/all/calendar", label: "予定", Icon: CalendarDays },
  { href: "/app/all/home", label: "ホーム", Icon: House },
  { href: "/app/all/docs", label: "ファイル", Icon: FolderOpen },
  { href: "/app/all/me", label: "マイページ", Icon: UserRound },
];

export function BottomNav() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { t } = useI18n();
  const [lastRoutes, setLastRoutes] = useState<Record<string, string>>({});
  const scope = pathScope(pathname);
  const currentSection = pathSection(pathname);
  const query = searchParams.toString();
  useEffect(() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem("hub:last-routes") ?? "{}") as Record<string, string>;
      setLastRoutes(stored);
    } catch { /* local history is optional */ }
  }, [scope]);
  useEffect(() => {
    if (!currentSection || !scope) return;
    const current = `${pathname}${query ? `?${query}` : ""}`;
    setLastRoutes((previous) => {
      const next = { ...previous, [`${scope}:${currentSection}`]: current };
      try { window.localStorage.setItem("hub:last-routes", JSON.stringify(next)); } catch { /* local history is optional */ }
      return next;
    });
  }, [currentSection, pathname, query, scope]);
  const labels = { "/app/all/home": t("home"), "/app/all/chat": t("chat"), "/app/all/calendar": t("calendar"), "/app/all/docs": t("docs"), "/app/all/me": t("myPage") };
  return (
    <nav
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] md:hidden"
      aria-label="モバイルナビゲーション"
    >
      <div className="pointer-events-auto flex w-full max-w-sm items-stretch gap-1 rounded-full border border-[color:var(--line)] bg-white/70 p-1 shadow-[0_6px_24px_rgba(18,18,18,0.12)] backdrop-blur-xl supports-[backdrop-filter]:bg-white/55">
        {items.map((it) => {
          const section = it.href.split("/")[3];
          const active = currentSection === section;
          const remembered = lastRoutes[`${scope}:${section}`];
          const href = remembered || `/app/${scope}/${section}`;
          const Icon = it.Icon;
          return (
            <Link
              key={it.href}
              href={href}
              aria-current={active ? "page" : undefined}
              className={clsx(
                "relative flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 rounded-full px-1 py-1 text-[10px] font-medium leading-none transition-[background-color,color,transform] duration-200 motion-reduce:transition-none",
                active
                  ? "bg-[color:var(--accent-soft)] text-[color:var(--accent-strong)] shadow-[0_1px_3px_rgba(18,18,18,0.10)] ring-1 ring-inset ring-[color:var(--accent-strong)]/15"
                  : "text-[color:var(--muted)] hover:bg-[color:var(--bg-subtle)] active:bg-[color:var(--bg-subtle)]",
              )}
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={active ? 2.4 : 2} aria-hidden="true" />
              <span className={clsx("max-w-full truncate", active && "font-semibold")}>{labels[it.href as keyof typeof labels]}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function pathScope(pathname: string) {
  const appMatch = pathname.match(/^\/app\/([^/]+)/);
  if (appMatch) return appMatch[1];
  const workspaceMatch = pathname.match(/^\/w\/([^/]+)/);
  return workspaceMatch?.[1] ?? "all";
}

function pathSection(pathname: string) {
  if (pathname.includes("/calendar")) return "calendar";
  if (pathname.includes("/chat")) return "chat";
  if (pathname.includes("/docs") || pathname.includes("/files")) return "docs";
  if (pathname.includes("/me") || pathname.includes("/settings")) return "me";
  if (pathname.includes("/home")) return "home";
  return null;
}
